import { useEffect, useRef, useState } from 'react';
import { bankReconApi, type ColumnInfo, type ColumnOverride, type ColumnRole, type ParsePreviewResult } from '../api/bankRecon';
import { ApiError } from '../api/client';

// Shown either automatically — when the initial preview's auto-detected
// column mapping looks uncertain (an unrecognized format) or actively
// suspect (narration that looks like it landed on the wrong column, see
// bank-statement-utils.ts's assessNarrationQuality(); or a suspiciously
// high share of candidate rows dropped as unparseable, see assessRowLoss())
// — or on demand, via ReportsPage.tsx's "Review column mapping…" link, for
// anyone who wants to double-check a confidently-auto-detected file before
// it's committed.
//
// Every role change re-calls the *same* preview endpoint the initial
// auto-detect used, with the user's current overrides — the server's own
// parsing logic (csv-parser.ts / pdf-table-parser.ts, both now built on the
// shared column-roles.ts classifier) stays the single source of truth for
// what a given mapping actually produces, so nothing about row extraction
// or narration assembly is duplicated into frontend TypeScript here.
const ROLE_OPTIONS: { value: ColumnRole; label: string }[] = [
  { value: 'date', label: 'Date' },
  { value: 'valueDate', label: 'Value Date' },
  { value: 'narration', label: 'Narration / Description' },
  { value: 'reference', label: 'Reference / Cheque No.' },
  { value: 'debit', label: 'Debit / Withdrawal' },
  { value: 'credit', label: 'Credit / Deposit' },
  { value: 'amount', label: 'Amount' },
  { value: 'drcr', label: 'Dr / Cr Sign' },
  { value: 'balance', label: 'Balance' },
  { value: 'serial', label: 'Serial No.' },
  { value: 'tranId', label: 'Transaction ID' },
  { value: 'other', label: 'Ignore' },
];

interface BankStatementMappingModalProps {
  file: File;
  password?: string;
  initialPreview: ParsePreviewResult;
  onClose: () => void;
  // Delegated to the caller so ReportsPage.tsx's existing success/warning
  // message + statement-list-refresh logic (doBankStatementUpload's tail)
  // stays in one place rather than being duplicated here. Should throw
  // (ApiError or otherwise) on failure so this modal can show the error
  // without closing.
  onConfirm: (overrides: ColumnOverride[]) => Promise<void>;
}

const th: React.CSSProperties = { textAlign: 'left', fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', padding: '0 0.6rem 0.5rem', borderBottom: '1px solid var(--border)' };
const td: React.CSSProperties = { padding: '0.5rem 0.6rem', borderBottom: '1px solid var(--border)', verticalAlign: 'top' };

export default function BankStatementMappingModal({ file, password, initialPreview, onClose, onConfirm }: BankStatementMappingModalProps) {
  const [columns, setColumns] = useState<ColumnInfo[]>(initialPreview.columns);
  const [sampleRows, setSampleRows] = useState(initialPreview.sampleRows);
  const [entryCount, setEntryCount] = useState(initialPreview.entryCount);
  const [parseSuspect, setParseSuspect] = useState(initialPreview.parseSuspect);
  const [parseSuspectReason, setParseSuspectReason] = useState(initialPreview.parseSuspectReason);
  const [ocrUsed, setOcrUsed] = useState(initialPreview.ocrUsed);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const debounceRef = useRef<number | undefined>(undefined);

  useEffect(() => () => { if (debounceRef.current) window.clearTimeout(debounceRef.current); }, []);

  function currentOverrides(cols: ColumnInfo[]): ColumnOverride[] {
    return cols.map(c => ({ index: c.index, role: c.role }));
  }

  function updateRole(index: number, role: ColumnRole) {
    const next = columns.map(c => (c.index === index ? { ...c, role } : c));
    setColumns(next);
    setConfirmError(null);
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(async () => {
      setRefreshing(true);
      setRefreshError(null);
      try {
        const preview = await bankReconApi.previewUpload(file, password, currentOverrides(next));
        setColumns(preview.columns);
        setSampleRows(preview.sampleRows);
        setEntryCount(preview.entryCount);
        setParseSuspect(preview.parseSuspect);
        setParseSuspectReason(preview.parseSuspectReason);
        setOcrUsed(preview.ocrUsed);
      } catch (err) {
        setRefreshError(err instanceof ApiError ? err.message : 'Could not re-parse this file with the updated mapping.');
      } finally {
        setRefreshing(false);
      }
    }, 400);
  }

  async function handleConfirm() {
    setConfirming(true);
    setConfirmError(null);
    try {
      await onConfirm(currentOverrides(columns));
      onClose();
    } catch (err) {
      setConfirmError(err instanceof ApiError ? err.message : 'Upload failed. Please try again.');
    } finally {
      setConfirming(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 720 }}>
        <div className="modal-header">
          <span className="modal-title">Review Column Mapping</span>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {ocrUsed && (
            <div className="warn-box">
              ⚠ This statement had no digital text layer and was read via OCR (optical character
              recognition) instead — please double-check every amount and date below carefully before
              uploading, since a misread digit can look like a perfectly plausible value.
            </div>
          )}
          {parseSuspect && (
            <div className="warn-box">
              ⚠ {parseSuspectReason ?? 'This file\'s auto-detected columns look uncertain — please double-check the mapping below before uploading.'}
            </div>
          )}
          {refreshError && <div className="error-box">{refreshError}</div>}

          <p style={{ fontSize: '0.85rem', color: 'var(--text-2)', margin: '0 0 0.9rem' }}>
            Detected <strong>{initialPreview.bankName}</strong> — {entryCount} transaction{entryCount === 1 ? '' : 's'}.
            Correct any column below that doesn't look right; the sample rows update automatically.
            This mapping will be remembered and applied automatically to future statements in this
            same format.
          </p>

          <div style={{ overflowX: 'auto', marginBottom: '1.1rem' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem', minWidth: 480 }}>
              <thead>
                <tr>
                  <th style={th}>Column</th>
                  <th style={th}>Sample value</th>
                  <th style={th}>This column is…</th>
                </tr>
              </thead>
              <tbody>
                {columns.map(c => (
                  <tr key={c.index}>
                    <td style={{ ...td, fontWeight: 700 }}>{c.label || `Column ${c.index + 1}`}</td>
                    <td style={{ ...td, color: 'var(--text-muted)', fontFamily: 'ui-monospace, SF Mono, Menlo, monospace', fontSize: '0.78rem' }}>
                      {c.sample[0] ?? '—'}
                    </td>
                    <td style={td}>
                      <select
                        value={c.role}
                        onChange={e => updateRole(c.index, e.target.value as ColumnRole)}
                        style={{ fontSize: '0.8rem', padding: '0.3rem 0.5rem' }}
                      >
                        {ROLE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
                {columns.length === 0 && (
                  <tr><td colSpan={3} style={{ ...td, color: 'var(--text-muted)', textAlign: 'center' }}>No columns detected in this file.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div style={{ fontSize: '0.76rem', fontWeight: 700, color: 'var(--text-muted)', marginBottom: '0.4rem' }}>
            Preview {refreshing && <span style={{ fontWeight: 400 }}>(updating…)</span>}
          </div>
          <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 6 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem', minWidth: 480 }}>
              <thead>
                <tr>
                  <th style={th}>Date</th>
                  <th style={th}>Description</th>
                  <th style={{ ...th, textAlign: 'right' }}>Debit</th>
                  <th style={{ ...th, textAlign: 'right' }}>Credit</th>
                </tr>
              </thead>
              <tbody>
                {sampleRows.map((r, i) => (
                  <tr key={i}>
                    <td style={td}>{r.date}</td>
                    <td style={{ ...td, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.description || '—'}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{r.debit !== undefined ? r.debit.toLocaleString('en-IN') : '—'}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{r.credit !== undefined ? r.credit.toLocaleString('en-IN') : '—'}</td>
                  </tr>
                ))}
                {sampleRows.length === 0 && (
                  <tr><td colSpan={4} style={{ ...td, color: 'var(--text-muted)', textAlign: 'center' }}>No transactions parsed with the current mapping.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="modal-footer">
          {confirmError && <span style={{ fontSize: '0.78rem', color: 'var(--danger)' }}>{confirmError}</span>}
          <div style={{ display: 'flex', gap: '0.6rem', flexShrink: 0, marginLeft: 'auto' }}>
            <button className="btn btn-ghost" onClick={onClose} disabled={confirming}>Cancel</button>
            <button className="btn btn-primary" onClick={handleConfirm} disabled={confirming || refreshing || entryCount === 0}>
              {confirming ? 'Uploading…' : 'Confirm & Upload'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
